// @ts-nocheck
/*
 * Create / Edit Call Group — dual-mode via location.state,
 * callcenter- prefix, alpha-num + underscore filter.
 * Group owner and manager are not collected on create.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { ArrowLeft, Minus, Loader2, Save, Users } from 'lucide-react';
import { API_AGENT_GROUPS_LIST } from '@/service/url';
import { postApiCall } from '@/service/postApiCall';
import { Storage } from '@/utilities/setting';
import { Button } from '@/components/ui/button';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';

export default function CreateAgentGroups() {
  const navigate = useNavigate();
  const location = useLocation();
  const agentGroupData: any = location.state || {};
  const isEditMode = Boolean(agentGroupData.id);

  const [groupName, setGroupName] = useState(agentGroupData.group_name || '');
  const [agents, setAgents] = useState<any[]>(agentGroupData.agent_id || []);
  const [callcenter, setCallCenter] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    try {
      const rawUser = Storage.getItem('user');
      const user = JSON.parse(rawUser);
      if (user && user.callcenter_id) {
        setCallCenter(user.callcenter_id);
      }
    } catch (err) {
      console.error('Failed to load user from storage:', err);
    }
  }, []);

  const handleSaveGroup = async () => {
    if (loading) return;
    if (!groupName?.trim()) return void toast.error('Group Name is required');
    try {
      const data = {
        group_name: `${callcenter}-${groupName}`,
        call_center_id: callcenter,
      };
      const apiUrl = isEditMode
        ? `${API_AGENT_GROUPS_LIST}/${agentGroupData.id}`
        : API_AGENT_GROUPS_LIST;
      setLoading(true);
      await postApiCall(apiUrl, data);
      if (isEditMode) {
        toast.success('Call group updated successfully', {
          description: 'If this call group is used in an active campaign, changes may take up to 5 minutes to fully sync.',
        });
      } else {
        toast.success('Call group created successfully');
      }
      setTimeout(() => navigate('/agent-group-list'), 1200);
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Error saving call group');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAgent = (index) => setAgents(agents.filter((_, i) => i !== index));

  const handleChange = (e) => {
    const value = e.target.value;
    const onlyValid = value.replace(/[^a-zA-Z0-9_]/g, '');
    setError(
      value !== onlyValid ? 'Only letters, numbers, and underscore (_) are allowed.' : '',
    );
    setGroupName(onlyValid);
  };

  return (
    <ScrollPage>
      <PageHeader
        title={isEditMode ? 'Edit Call Group' : 'Create Call Group'}
        description="Configure a named group of agents used for routing and campaigns."
        actions={
          <Button asChild variant="outline">
            <Link to="/agent-group-list">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </Button>
        }
      />

      <div
        className="rounded-2xl border border-border/60 bg-card p-5"
        style={{
          boxShadow:
            '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
        }}
      >
        <div className="mb-4 flex items-center gap-3 border-b border-border/50 pb-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-inset ring-primary/25">
            <Users className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-foreground">
              {isEditMode ? 'Update call group' : 'New call group'}
            </h3>
            <p className="text-[11px] text-muted-foreground">
              Group name is auto-prefixed with your call center id.
            </p>
          </div>
        </div>

        <form
          onSubmit={(e) => e.preventDefault()}
          className="grid gap-4 md:max-w-md"
        >
          <div className="space-y-1.5">
            <label htmlFor="groupName" className="text-xs font-semibold text-foreground">
              Group Name <span className="text-destructive">*</span>
            </label>
            {/* Call-center prefix sits inside the field as fixed text before the typed name. */}
            <label htmlFor="groupName" className="input-embossed flex h-10 w-full cursor-text items-center rounded-md pl-3 text-sm">
              <span className="select-none text-muted-foreground">{callcenter}-</span>
              <input
                type="text"
                id="groupName"
                value={groupName}
                onChange={handleChange}
                placeholder="Enter group name"
                className="h-full min-w-0 flex-1 border-0 bg-transparent pl-1 pr-3 text-sm outline-none"
              />
            </label>
            {error && <p className="text-[11px] text-destructive">{error}</p>}
          </div>
        </form>

        {agents.length > 0 && (
          <div className="mt-5">
            <h4 className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Existing agents
            </h4>
            <div className="max-h-[240px] overflow-auto rounded-lg border border-border/60 bg-card">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-primary/[0.06] text-[10px] uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-2 text-left font-semibold">Agent Name</th>
                    <th className="px-3 py-2 text-right font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {agents.map((agent, index) => (
                    <tr
                      key={index}
                      className={`border-t border-border/40 ${index % 2 ? 'bg-muted/30' : ''}`}
                    >
                      <td className="px-3 py-2 font-medium text-foreground">
                        {agent.agent_name}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          title="Remove agent"
                          onClick={() => handleDeleteAgent(index)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200 transition hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300"
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="mt-5 flex items-center justify-end gap-2 border-t border-border/50 pt-4">
          <Button asChild variant="outline">
            <Link to="/agent-group-list">Cancel</Link>
          </Button>
          <Button
            onClick={handleSaveGroup}
            type="button"
            disabled={loading}
            className="gap-1.5"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {loading
              ? isEditMode
                ? 'Updating…'
                : 'Creating…'
              : isEditMode
                ? 'Save changes'
                : 'Create group'}
          </Button>
        </div>
      </div>
    </ScrollPage>
  );
}
